// components/shop/ProductGridBit.jsx
import React, {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import LuxuryLoading from '../ui/LuxuryLoading';
import LuxuryButton from '../ui/LuxuryButton';
import {cardEntrance, staggerContainer} from '../animations/animations';
import ProductCardBit from './ProductCardBit';
import {CartService, cartEvents} from "../../api/cart.api";
import '../../styles/globals.css';

const ProductGridBit = ({
                            filteredProducts = [],
                            isFiltering = false,
                            products = [],
                            showHeader = true,
                            showLoadMore = true
                        }) => {
    const [displayProducts, setDisplayProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [cartLoading, setCartLoading] = useState({});
    const [cartSummary, setCartSummary] = useState({totalItems: 0});
    const [addToCartResults, setAddToCartResults] = useState({});
    const productsPerPage = 12;
    const navigate = useNavigate();

    useEffect(() => {
        if (filteredProducts.length > 0) {
            setDisplayProducts(filteredProducts.slice(0, productsPerPage));
            setIsLoading(false);
            setHasMore(filteredProducts.length > productsPerPage);
        } else if (products.length > 0) {
            setDisplayProducts(products.slice(0, productsPerPage));
            setIsLoading(false);
            setHasMore(products.length > productsPerPage);
        } else {
            setIsLoading(true);
        }
    }, [filteredProducts, products]);

    // Load cart summary on mount
    useEffect(() => {
        loadCartSummary();

        // Subscribe to cart updates
        const unsubscribe = cartEvents.subscribe('cartUpdated', () => {
            loadCartSummary();
        });

        return () => unsubscribe();
    }, []);

    const loadCartSummary = async () => {
        try {
            const summary = await CartService.getCartSummary();
            setCartSummary(summary);
        } catch (error) {
            console.error('Error loading cart summary:', error);
        }
    };

    const loadMoreProducts = async () => {
        if (loadingMore || !hasMore) return;

        try {
            setLoadingMore(true);
            const nextPage = page + 1;
            const startIndex = (nextPage - 1) * productsPerPage;
            const endIndex = startIndex + productsPerPage;

            const sourceProducts = filteredProducts.length > 0 ? filteredProducts : products;

            if (sourceProducts.length > endIndex) {
                const newProducts = sourceProducts.slice(startIndex, endIndex);
                setDisplayProducts(prev => [...prev, ...newProducts]);
                setPage(nextPage);
                setHasMore(sourceProducts.length > endIndex);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error loading more products:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleAddToCart = async (productId, variantId = 'default', productData = null) => {
        try {
            setCartLoading(prev => ({...prev, [productId]: true}));
            setAddToCartResults(prev => ({...prev, [productId]: null}));

            // Get the appropriate variant
            let selectedVariantId = variantId;
            if (variantId === 'default' && productData?.variants?.length > 0) {
                // Use the default variant from stockInfo if available, otherwise first variant
                if (productData.stockInfo?.defaultVariant) {
                    selectedVariantId = productData.stockInfo.defaultVariant.id;
                } else {
                    selectedVariantId = productData.variants[0].id;
                }
            }

            // Check if the variant is in stock
            const variantInStock = isVariantInStock(productData, selectedVariantId);

            if (!variantInStock) {
                throw new Error('This item is currently out of stock');
            }

            // Add to cart using the CartService
            const result = await CartService.addToCart({
                productId,
                variantId: selectedVariantId,
                quantity: 1
            });

            // Store the result for this product
            setAddToCartResults(prev => ({
                ...prev,
                [productId]: {
                    success: true,
                    cart: result.cart,
                    results: result.results,
                    errors: result.errors,
                    message: result.message
                }
            }));

            // Show success/error message
            if (result.errors && result.errors.length > 0) {
                const error = result.errors[0];
                console.warn(`❌ Failed to add ${productData?.name}: ${error.error}`);

                // Show error message for a few seconds
                setTimeout(() => {
                    setAddToCartResults(prev => ({...prev, [productId]: null}));
                }, 3000);
            } else {
                console.log(`✅ Added ${productData?.name || 'item'} to cart`);

                // Emit cart update event
                cartEvents.emit('itemAdded', {
                    productId,
                    variantId: selectedVariantId,
                    productName: productData?.name,
                    quantity: 1
                });

                navigate('/cart');

            }

            // Refresh cart summary
            await loadCartSummary();

            return result;
        } catch (error) {
            console.error('Error adding to cart:', error);

            // Store error result
            setAddToCartResults(prev => ({
                ...prev,
                [productId]: {
                    success: false,
                    error: error.message
                }
            }));

            // Clear error message after 3 seconds
            setTimeout(() => {
                setAddToCartResults(prev => ({...prev, [productId]: null}));
            }, 3000);

            throw error;
        } finally {
            setCartLoading(prev => ({...prev, [productId]: false}));
        }
    };

    const handleQuickAdd = async (productId, productData = null) => {
        // Quick add with default variant
        return handleAddToCart(productId, 'default', productData);
    };

    const handleViewProduct = (product) => {
        // Priority: slug > id > _id
        const productSlug = product.slug || product.id || product._id;
        navigate(`/product/${productSlug}`, {
            state: { product },
            replace: false
        });
    };


    // Helper function to check if a variant is in stock
    const isVariantInStock = (product, variantId) => {
        if (!product || !product.stockInfo) return false;

        // If variantId is provided, check specific variant
        if (variantId && variantId !== 'default') {
            const inventoryItem = product.stockInfo.inventory?.find(
                inv => inv.variantId === variantId
            );
            return inventoryItem?.canFulfill || false;
        }

        // Otherwise check overall product stock
        return product.stockInfo.isInStock || false;
    };

    // Helper function to get variant stock quantity
    const getVariantStock = (product, variantId) => {
        if (!product || !product.stockInfo || !product.stockInfo.inventory) return 0;

        const inventoryItem = product.stockInfo.inventory?.find(
            inv => inv.variantId === variantId
        );
        return inventoryItem?.quantityAvailable || 0;
    };

    // Helper function to get variant stock status
    const getVariantStockStatus = (product, variantId) => {
        const quantity = getVariantStock(product, variantId);
        const inventoryItem = product.stockInfo?.inventory?.find(
            inv => inv.variantId === variantId
        );

        if (quantity <= 0) return 'out-of-stock';
        if (inventoryItem?.isLowStock) return 'low-stock';
        return 'in-stock';
    };

    // Helper function to get the best available variant
    const getBestAvailableVariant = (product) => {
        if (!product || !product.variants) return null;

        // Try to find a variant that's in stock
        const inStockVariant = product.variants.find(variant =>
            isVariantInStock(product, variant.id)
        );

        // If no variant is in stock, return the default or first variant
        return inStockVariant ||
            product.stockInfo?.defaultVariant ||
            (product.variants.length > 0 ? product.variants[0] : null);
    };

    // Format product data from API response
    const formatProductData = (product) => {
        // Get best available variant
        const bestVariant = getBestAvailableVariant(product);
        const variantPrice = bestVariant?.price || 0;

        const price = variantPrice || product.price || product.discountPrice || 0;
        const originalPrice = product.originalPrice || product.price || 0;
        const discount = product.discount ||
            (originalPrice > price ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0);

        // Determine stock status
        const isProductInStock = product.stockInfo?.isInStock || false;
        const isLowStock = product.stockInfo?.isLowStock || false;
        const totalAvailable = product.stockInfo?.totalAvailable || 0;

        // Get available variants
        const availableVariants = (product.variants || []).map(variant => ({
            ...variant,
            inStock: isVariantInStock(product, variant.id),
            stock: getVariantStock(product, variant.id),
            stockStatus: getVariantStockStatus(product, variant.id)
        }));

        return {
            id: product.id || product._id,
            _id: product._id || product.id,
            name: product.name,
            slug: product.slug,
            category: product.category,
            description: product.description,
            price: price,
            originalPrice: originalPrice,
            discount: discount,
            discountPrice: product.discountPrice,
            rating: product.rating || 0,
            reviewCount: product.reviewCount || 0,
            images: product.images || [],
            variants: availableVariants,
            concerns: product.concerns || [],
            ingredients: product.ingredients || [],
            freeFrom: product.freeFrom || [],
            benefits: product.benefits || [],
            detailedDescription: product.detailedDescription,
            isNew: product.isNew || false,
            isBestseller: product.isBestseller || false,
            isFeatured: product.isFeatured || false,

            // Stock information
            inStock: isProductInStock,
            isLowStock: isLowStock,
            stockInfo: product.stockInfo || {
                totalAvailable: 0,
                totalReserved: 0,
                totalStock: 0,
                isInStock: false,
                isLowStock: false,
                outOfStockVariants: 0,
                inventory: [],
                defaultVariant: bestVariant
            },

            // Legacy inventory field for compatibility
            inventory: {
                quantityAvailable: totalAvailable,
                canFulfill: isProductInStock
            },

            // Best variant information
            bestVariant: bestVariant ? {
                id: bestVariant.id,
                name: bestVariant.name,
                price: bestVariant.price,
                inStock: isVariantInStock(product, bestVariant.id),
                stock: getVariantStock(product, bestVariant.id)
            } : null
        };
    };

    // Get stock status badge text and color
    const getStockStatusBadge = (product) => {
        if (!product.stockInfo || !product.stockInfo.isInStock) {
            return {
                text: 'Out of Stock',
                color: 'text-charcoal/60',
                bgColor: 'bg-charcoal/5',
                borderColor: 'border-charcoal/10'
            };
        }

        if (product.stockInfo.isLowStock) {
            return {
                text: 'Low Stock',
                color: 'text-amber-600',
                bgColor: 'bg-amber-50',
                borderColor: 'border-amber-200'
            };
        }

        return {
            text: 'In Stock',
            color: 'text-sage',
            bgColor: 'bg-sage/10',
            borderColor: 'border-sage/20'
        };
    };

    // Get cart status for a specific product
    const getProductCartStatus = (productId) => {
        const result = addToCartResults[productId];
        if (!result) return null;

        if (result.success) {
            if (result.errors && result.errors.length > 0) {
                return {
                    type: 'error',
                    message: result.errors[0].error
                };
            }
            return {
                type: 'success',
                message: result.message || 'Added to cart!'
            };
        } else {
            return {
                type: 'error',
                message: result.error
            };
        }
    };

    // Show loading state
    if (isLoading && displayProducts.length === 0) {
        return (
            <div className="min-h-[400px] flex items-center justify-center">
                <LuxuryLoading size="large"/>
            </div>
        );
    }

    // Show empty state
    if (displayProducts.length === 0 && !isLoading) {
        return (
            <motion.div
                className="empty-state"
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6}}
            >
                <div className="text-center py-16">
                    <motion.div
                        animate={{y: [0, -10, 0]}}
                        transition={{duration: 2, repeat: Infinity}}
                        className="mb-6"
                    >
                        <div
                            className="w-16 h-16 mx-auto bg-gradient-to-r from-champagne/10 to-sage/10 rounded-full flex items-center justify-center">
                            <span className="text-2xl text-charcoal/40">😔</span>
                        </div>
                    </motion.div>

                    <h3 className="text-2xl font-heading text-charcoal mb-3">
                        No Products Found
                    </h3>

                    <p className="text-charcoal/60 max-w-md mx-auto mb-8">
                        We couldn't find any products matching your criteria.
                        Try adjusting your filters or browse our full collection.
                    </p>

                    <LuxuryButton
                        variant="primary"
                        onClick={() => {
                            window.location.reload();
                        }}
                    >
                        View All Products
                    </LuxuryButton>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            id="products-grid"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
        >
            {/* Results Count - Only show if header is enabled */}
            {showHeader && (
                <motion.div
                    className="results-count mb-8 pb-6 border-b border-nude/50"
                    variants={{
                        hidden: {opacity: 0, y: -20},
                        visible: {
                            opacity: 1,
                            y: 0,
                            transition: {duration: 0.5}
                        }
                    }}
                >
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <span className="text-charcoal/80">
                                Showing <strong>{displayProducts.length}</strong> of{' '}
                                <strong>{filteredProducts.length > 0 ? filteredProducts.length : products.length}</strong> products
                            </span>

                            {/* Show in-stock count */}
                            <span className="text-sage text-sm">
                                {displayProducts.filter(p => p.stockInfo?.isInStock).length} in stock
                            </span>

                            <div className="flex items-center gap-2 text-sage">
                                <span className="text-sm">Cart: {cartSummary.totalItems || 0} items</span>
                            </div>
                        </div>

                        {isFiltering && (
                            <motion.div
                                initial={{opacity: 0}}
                                animate={{opacity: 1}}
                                className="flex items-center gap-2 text-sage"
                            >
                                <LuxuryLoading size="small"/>
                                <span className="text-sm">Updating results...</span>
                            </motion.div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Products Grid */}
            <div className="products-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                <AnimatePresence mode="wait">
                    {displayProducts.map((product, index) => {
                        const formattedProduct = formatProductData(product);
                        const cartStatus = getProductCartStatus(formattedProduct.id);
                        const isAddingToCart = cartLoading[formattedProduct.id];
                        const stockStatus = getStockStatusBadge(formattedProduct);

                        return (
                            <motion.div
                                key={`${formattedProduct.id}-${index}`}
                                layout
                                initial="hidden"
                                animate="visible"
                                exit={{opacity: 0, scale: 0.9}}
                                transition={{duration: 0.3}}
                                variants={cardEntrance(index)}
                                className="h-full relative"
                            >

                                {/* Low Stock Badge */}
                                {formattedProduct.isLowStock && formattedProduct.inStock && (
                                    <div className="absolute top-3 left-3 z-10">
                                        <span
                                            className={`px-3 py-1 text-xs font-medium rounded-full ${stockStatus.bgColor} ${stockStatus.color} border ${stockStatus.borderColor}`}>
                                            {stockStatus.text}
                                        </span>
                                    </div>
                                )}

                                <ProductCardBit
                                    product={formattedProduct}
                                    index={index}
                                    onAddToCart={(e, variantId) => {
                                        e?.stopPropagation();
                                        return handleAddToCart(
                                            formattedProduct.id,
                                            variantId || 'default',
                                            formattedProduct
                                        );
                                    }}
                                    onQuickAdd={(e) => {
                                        e?.stopPropagation();
                                        return handleQuickAdd(formattedProduct.id, formattedProduct);
                                    }}
                                    onViewProduct={() => handleViewProduct(formattedProduct)}
                                    isLoading={isAddingToCart}
                                    showQuickView={true}
                                    cartStatus={cartStatus}
                                    isOutOfStock={!formattedProduct.inStock}
                                    isLowStock={formattedProduct.isLowStock}
                                />
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            {/* Load More Button - Only show if enabled */}
            {showLoadMore && hasMore && (
                <motion.div
                    className="load-more-container text-center py-8"
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.5}}
                >
                    <LuxuryButton
                        variant="secondary"
                        size="large"
                        onClick={loadMoreProducts}
                        loading={loadingMore}
                        disabled={loadingMore}
                        className="min-w-48"
                    >
                        {loadingMore ? (
                            <span className="flex items-center gap-2">
                                <LuxuryLoading size="small"/>
                                Loading...
                            </span>
                        ) : (
                            'Load More Products'
                        )}
                    </LuxuryButton>
                </motion.div>
            )}

            {/* End of Results */}
            {!hasMore && displayProducts.length > 0 && (
                <motion.div
                    className="end-of-results text-center py-12 border-t border-nude/30"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{delay: 0.3}}
                >
                    <p className="text-charcoal/60">
                        You've reached the end of our collection
                    </p>
                    <p className="text-sm text-charcoal/40 mt-2">
                        Discover more luxury rituals in our seasonal collections
                    </p>
                </motion.div>
            )}
        </motion.div>
    );
};

// Default props
ProductGridBit.defaultProps = {
    showHeader: true,
    showLoadMore: true
};

export default ProductGridBit;